import { useRef } from "react";
import { X, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface ImageUploaderProps {
  images: File[];
  onImagesChange: (images: File[]) => void;
  maxImages?: number;
  disabled?: boolean;
}

export function ImageUploader({ images, onImagesChange, maxImages = 4, disabled }: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => {
      if (!f.type.startsWith('image/')) {
        toast({ title: "Arquivo inválido", description: `${f.name} não é uma imagem`, variant: "destructive" });
        return false;
      }
      if (f.size > 2 * 1024 * 1024) {
        toast({ title: "Arquivo muito grande", description: `${f.name} excede 2MB`, variant: "destructive" });
        return false;
      }
      return true;
    });

    // Check dimensions for each valid file
    validFiles.forEach(f => {
      const img = new Image();
      img.onload = () => {
        if (img.width !== 1080 || img.height !== 1080) {
          toast({
            title: "Dimensão recomendada: 1080x1080",
            description: `${f.name} tem ${img.width}x${img.height}px. Para melhor qualidade, use 1080x1080.`,
          });
        }
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(f);
    });

    const total = images.length + validFiles.length;
    if (total > maxImages) {
      toast({ title: "Limite de imagens", description: `Máximo de ${maxImages} imagens`, variant: "destructive" });
      onImagesChange([...images, ...validFiles.slice(0, maxImages - images.length)]);
    } else {
      onImagesChange([...images, ...validFiles]);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    onImagesChange(images.filter((_, i) => i !== index));
  };

  if (images.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className={`grid gap-2 ${images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
        {images.map((file, i) => (
          <div key={i} className="relative group rounded-lg overflow-hidden border border-border">
            <img
              src={URL.createObjectURL(file)}
              alt={`Preview ${i + 1}`}
              className="w-full h-32 object-cover"
            />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => removeImage(i)}
              disabled={disabled}
              className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
        {images.length < maxImages && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            className="h-32 border-2 border-dashed border-border rounded-lg flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          >
            <ImagePlus className="h-6 w-6" />
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Resolução recomendada: 1080x1080px · Máximo 2MB por imagem</p>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}

// Expose a way to trigger the file picker externally
ImageUploader.openPicker = (inputRef: React.RefObject<HTMLInputElement>) => {
  inputRef.current?.click();
};
